import React, { useRef, useState, useEffect } from "react";

interface TabsProps {
  tabs: string[];
  selectedTabIndex?: number;
  onSelectedTabChanged: (selectedTabIndex: number) => void;
}

const Tabs = ({
  tabs,
  selectedTabIndex = 0,
  onSelectedTabChanged,
}: TabsProps) => {
  const tabsRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLLIElement | null)[]>([]);
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (tabsRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = tabsRef.current;
        setShowLeftButton(scrollLeft > 0);
        setShowRightButton(scrollLeft + clientWidth < scrollWidth);
      }
    };

    if (tabsRef.current) {
      tabsRef.current.addEventListener("scroll", handleScroll);
      handleScroll(); // Initial check
    }

    return () => {
      if (tabsRef.current) {
        tabsRef.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    if (tabRefs.current[selectedTabIndex]) {
      tabRefs.current[selectedTabIndex]?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [selectedTabIndex]);

  const scrollLeft = () => {
    if (tabsRef.current) {
      tabsRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (tabsRef.current) {
      tabsRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  function getTabClassName(tabIndex: number) {
    if (selectedTabIndex === tabIndex) return "selected";
    return "";
  }

  return (
    <div className="tabsContainer">
      {showLeftButton && (
        <button className="scrollButton left" onClick={scrollLeft}>
          &lt;
        </button>
      )}
      <div className="tabs" ref={tabsRef}>
        <ul>
          {tabs.map((x, i) => (
            <li
              key={i}
              ref={(el) => (tabRefs.current[i] = el)}
              className={getTabClassName(i)}
              onClick={() => onSelectedTabChanged(i)}
            >
              {x}
            </li>
          ))}
        </ul>
      </div>
      {showRightButton && (
        <button className="scrollButton right" onClick={scrollRight}>
          &gt;
        </button>
      )}
    </div>
  );
};

export default Tabs;